// Resolves the checkout V4 "picker badge" settings into <PromoBadge> props.
const STYLE_DEFAULTS = {
  upgrade: {
    cta: "UNLOCK SAVINGS",
    desc: "Upgrade to ###react_quantity###x & Save ###react_savings### + Free Shipping 🎁",
    bannerBg: "#ffeec4",
    ctaBg: "#ffc438",
    textColor: "#000000",
  },
  target: {
    cta: "SAVE BIG",
    desc: "Save ###react_savings### + Free Shipping 🎁",
    bannerBg: "#bfe6b8",
    ctaBg: "#216a13",
    textColor: "#ffffff",
  },
};

const products = () => (typeof window !== "undefined" ? window.post_template?.checkout_v4_products : null) || null;

/**
 * Feature toggle for the promotional banner beneath the selected option.
 * Two independent ACF toggles: the base one covers normal / one-time
 * purchases, the subscription one covers the "Subscription" purchase type
 * on subscription checkouts — so badges can run on either mode or both.
 */
export const isPickerBadgeEnabled = (isSubscribe = false) => {
  const p = products();
  return isSubscribe
    ? !!p?.checkout_v4_products_picker_badge_subscription_enable
    : !!p?.checkout_v4_products_picker_badge_enable;
};

/** Whether the lower options' banners click through to the target. On by default. */
export const isPickerBadgeLinkEnabled = () => {
  const v = products()?.checkout_v4_products_picker_badge_link_enable;
  return v === undefined || v === null ? true : !!v;
};

/**
 * 0-based index of the target option (green, no link). Driven by the optional
 * 1-based ACF "Target Picker Option"; defaults to the last option.
 * @param {number} optionsCount
 */
export const getPickerBadgeTargetIndex = (optionsCount) => {
  const last = Math.max(optionsCount - 1, 0);
  const n = parseInt(products()?.checkout_v4_products_picker_badge_target, 10);
  if (Number.isInteger(n) && n >= 1) return Math.min(n - 1, last);
  return last;
};

/** Opt-in for the per-option "Upgrade To Option" repeater values. Off by default. */
export const isPickerBadgeCustomTargetsEnabled = () =>
  !!products()?.checkout_v4_products_picker_badge_custom_targets_enable;

/**
 * 0-based upgrade target for one option. With custom targets enabled, the
 * mode's repeater row may point anywhere ahead (1-based "Upgrade To Option"),
 * or 0 for "no upgrade" — the option becomes its own target (green banner, no
 * link). Blank or invalid values, or the toggle being off, fall back to the
 * shared global target, so a half-filled repeater degrades to the old behavior.
 * @param {number} index 0-based option position
 * @param {number} optionsCount
 * @param {boolean} isSubscribe
 */
export const getPickerBadgeUpgradeTargetIndex = (index, optionsCount, isSubscribe = false) => {
  const globalTarget = getPickerBadgeTargetIndex(optionsCount);
  if (!isPickerBadgeCustomTargetsEnabled()) return globalTarget;

  const p = products() || {};
  const rows = isSubscribe ? p.checkout_v4_products_picker_badge_sub_items : p.checkout_v4_products_picker_badge_items;
  const raw = Array.isArray(rows) ? rows[index]?.badge_upgrade_target : undefined;
  if (raw === undefined || raw === null || raw === "") return globalTarget;

  const n = parseInt(raw, 10);
  if (n === 0) return index;
  if (Number.isInteger(n) && n - 1 > index && n - 1 <= optionsCount - 1) return n - 1;
  return globalTarget;
};

// Format a product's dollar savings (old_price - price) with the locale's
// currency template, e.g. "$0.00" -> "$222.80". Returns "" when there's none.
const formatSavings = (product, formattedString) => {
  const saved = Number(product?.old_price) - Number(product?.price);
  if (!Number.isFinite(saved) || saved <= 0) return "";
  const value = saved.toFixed(2);
  return formattedString ? formattedString.replace(/(0\.00|0,00|0)/, value) : value;
};

const applyTokens = (template, { quantity, savings }) =>
  (template || "").replace(/###react_quantity###/g, quantity ?? "").replace(/###react_savings###/g, savings ?? "");

const firstNonEmpty = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

/**
 * Build <PromoBadge> props for one option, or null when disabled.
 * @returns {{ variant: "upgrade"|"target", ctaText: string, description: string, styles: { backgroundColor: string, ctaColor: string, textColor: string } } | null}
 */
export const buildPickerBadge = (index, targetIndex, list, productData, isSubscribe = false) => {
  if (!isPickerBadgeEnabled(isSubscribe)) return null;

  const p = products() || {};
  const isUpgrade = index < targetIndex;
  const def = isUpgrade ? STYLE_DEFAULTS.upgrade : STYLE_DEFAULTS.target;

  // Subscription-mode copy overrides (ACF tr_ fields); blank falls through to
  // the shared defaults below.
  const subCta = isSubscribe
    ? isUpgrade
      ? p.tr_checkout_v4_products_picker_badge_sub_cta
      : p.tr_checkout_v4_products_picker_badge_sub_target_cta
    : undefined;
  const subDesc = isSubscribe
    ? isUpgrade
      ? p.tr_checkout_v4_products_picker_badge_sub_description
      : p.tr_checkout_v4_products_picker_badge_sub_target_description
    : undefined;

  // Translatable global defaults (ACF tr_ fields), per role.
  const globalCta = isUpgrade
    ? p.tr_checkout_v4_products_picker_badge_cta
    : p.tr_checkout_v4_products_picker_badge_target_cta;
  const globalDesc = isUpgrade
    ? p.tr_checkout_v4_products_picker_badge_description
    : p.tr_checkout_v4_products_picker_badge_target_description;

  // Per-option overrides come from the mode's own repeater: the subscription
  // one while "Subscription" is selected, the regular one otherwise.
  const rows = isSubscribe ? p.checkout_v4_products_picker_badge_sub_items : p.checkout_v4_products_picker_badge_items;
  const row = Array.isArray(rows) ? rows[index] : null;
  const rowDescription = row?.tr_badge_description;
  const isRichDescription = rowDescription !== undefined && rowDescription !== null && rowDescription !== "";

  // Savings/quantity refer to the target for upgrade banners, else this option.
  const refOption = (isUpgrade ? list?.[targetIndex] : list?.[index]) || {};
  const tokens = { quantity: refOption.amount, savings: formatSavings(refOption, productData?.formatted_string) };

  return {
    variant: isUpgrade ? "upgrade" : "target",
    ctaText: firstNonEmpty(row?.tr_badge_cta, subCta, globalCta, def.cta),
    description: applyTokens(firstNonEmpty(rowDescription, subDesc, globalDesc, def.desc), tokens),
    richDescription: isRichDescription,
    styles: {
      backgroundColor: firstNonEmpty(row?.badge_banner_bg, def.bannerBg),
      ctaColor: firstNonEmpty(row?.badge_cta_bg, def.ctaBg),
      textColor: firstNonEmpty(row?.badge_text_color, def.textColor),
    },
  };
};
